import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import DashWelcome from '../components/DashWelcome';
import TabNavigation from '../components/TabNavigation';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Video, Star, History, Play, Clock, Tag as TagIcon } from 'lucide-react';
import { Tag } from '../types/video';

interface VideoStats {
  totalVideos: number;
  favoriteCount: number;
  watchedCount: number;
  continueWatching: number;
}

export function DashLibrary() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<VideoStats>({
    totalVideos: 0,
    favoriteCount: 0,
    watchedCount: 0,
    continueWatching: 0,
  });
  const [featuredVideos, setFeaturedVideos] = useState<any[]>([]);
  const [categories, setCategories] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadLibraryData();
  }, []);

  async function loadLibraryData() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [videosData, favoritesData, progressData, featuredData, tagsData] = await Promise.all([
        supabase.from('videos').select('id').eq('is_published', true),
        supabase.from('user_favorites').select('id').eq('user_id', user.id),
        supabase.from('user_video_progress').select('id, completed').eq('user_id', user.id),
        supabase
          .from('videos')
          .select('*')
          .eq('is_published', true)
          .eq('is_featured', true)
          .limit(3),
        supabase.from('tags').select('*').order('name'),
      ]);

      const continueWatching = progressData.data?.filter((p) => !p.completed).length || 0;
      const watched = progressData.data?.filter((p) => p.completed).length || 0;

      setStats({
        totalVideos: videosData.data?.length || 0,
        favoriteCount: favoritesData.data?.length || 0,
        watchedCount: watched,
        continueWatching,
      });

      setFeaturedVideos(featuredData.data || []);
      setCategories(tagsData.data || []);
    } catch (error) {
      console.error('Error loading library data:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="text-gray-600 mt-4">Loading your library...</p>
        </div>
      </div>
    );
  }

  return (
    <ProtectedLayout>
      <div className="p-4 max-w-4xl mx-auto space-y-6">
        <DashWelcome />

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Media Library</h2>
            <Video className="text-blue-600" size={32} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <button
              onClick={() => navigate('/dashboard/videos?filter=all')}
              className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center hover:shadow-md transition-all cursor-pointer group"
            >
              <Video className="text-blue-600 mx-auto mb-2 group-hover:scale-110 transition-transform" size={28} />
              <div className="text-2xl font-bold text-gray-900">{stats.totalVideos}</div>
              <div className="text-sm text-gray-600">Total Videos</div>
            </button>

            <button
              onClick={() => navigate('/dashboard/videos?filter=favorites')}
              className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 text-center hover:shadow-md transition-all cursor-pointer group"
            >
              <Star className="text-yellow-600 mx-auto mb-2 fill-current group-hover:scale-110 transition-transform" size={28} />
              <div className="text-2xl font-bold text-gray-900">{stats.favoriteCount}</div>
              <div className="text-sm text-gray-600">Favorites</div>
            </button>

            <button
              onClick={() => navigate('/dashboard/videos?filter=completed')}
              className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center hover:shadow-md transition-all cursor-pointer group"
            >
              <History className="text-green-600 mx-auto mb-2 group-hover:scale-110 transition-transform" size={28} />
              <div className="text-2xl font-bold text-gray-900">{stats.watchedCount}</div>
              <div className="text-sm text-gray-600">Completed</div>
            </button>

            <button
              onClick={() => navigate('/dashboard/videos?filter=in-progress')}
              className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 text-center hover:shadow-md transition-all cursor-pointer group"
            >
              <Play className="text-purple-600 mx-auto mb-2 group-hover:scale-110 transition-transform" size={28} />
              <div className="text-2xl font-bold text-gray-900">{stats.continueWatching}</div>
              <div className="text-sm text-gray-600">In Progress</div>
            </button>
          </div>

          {categories.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <TagIcon className="text-gray-700" size={20} />
                <h3 className="text-lg font-semibold text-gray-900">Browse by Category</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => navigate(`/dashboard/videos/watch?category=${category.id}`)}
                    className="px-4 py-2 rounded-full text-sm font-medium transition-all hover:shadow-md group"
                    style={{
                      backgroundColor: category.color + '20',
                      color: category.color,
                      borderWidth: '2px',
                      borderColor: category.color + '40',
                    }}
                  >
                    <span className="group-hover:scale-105 inline-block transition-transform">
                      {category.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {featuredVideos.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Featured Videos</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {featuredVideos.map((video) => (
                  <div
                    key={video.id}
                    onClick={() => navigate(`/dashboard/videos/${video.id}`)}
                    className="group cursor-pointer bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all"
                  >
                    <div className="relative aspect-video bg-gray-100">
                      {video.thumbnail_url ? (
                        <img
                          src={video.thumbnail_url}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play size={32} className="text-gray-400" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                        <Play
                          size={32}
                          className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                      </div>
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1">
                        {video.title}
                      </h4>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Clock size={12} />
                        {formatDuration(video.duration)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <TabNavigation />
      </div>
    </ProtectedLayout>
  );
}
